<template>
  <v-navigation-drawer
    v-model="drawerSync"
    app
    :mini-variant="miniSync"
    :width="260"
    :permanent="$vuetify.breakpoint.lgAndUp"
    :temporary="$vuetify.breakpoint.mdAndDown"
    :dark="$vuetify.theme.dark"
    :color="$vuetify.theme.dark ? 'grey darken-4' : 'white'"
    class="sidebar-new"
  >
    <!-- User Profile Section -->
    <template v-slot:prepend v-if="!miniSync">
      <v-list dense class="pa-0">
        <v-list-item class="px-2 py-4 user-profile-section">
          <v-list-item-avatar size="48">
            <v-avatar
              :color="$vuetify.theme.dark ? 'primary' : 'primary lighten-1'"
              size="48"
            >
              <v-img v-if="user && user.photo" :src="user.photo"></v-img>
              <span v-else class="white--text headline">
                {{ user && user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
              </span>
            </v-avatar>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">
              {{ user.name || 'User' }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ user.username || 'username' }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon small @click="toggleDarkMode">
              <v-icon small>
                {{ $vuetify.theme.dark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}
              </v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-divider />
    </template>

    <!-- Logo Section for mini variant -->
    <template v-slot:prepend v-else>
      <v-list dense class="pa-0">
        <v-list-item class="px-2 py-3 justify-center">
          <v-avatar size="40">
            <img src="@/assets/logo/icon-geebung.png" alt="Logo">
          </v-avatar>
        </v-list-item>
      </v-list>
      <v-divider />
    </template>

    <!-- Navigation Menu -->
    <v-list dense shaped class="py-1">
      <!-- Dashboard -->
      <v-list-item
        :to="{ name: 'dashboard' }"
        exact
        class="mb-1"
      >
        <v-list-item-icon>
          <v-icon>mdi-view-dashboard</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('link.dashboard') }}</v-list-item-title>
        </v-list-item-content>
        <v-tooltip v-if="miniSync" right>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
          </template>
          <span>{{ $t('link.dashboard') }}</span>
        </v-tooltip>
      </v-list-item>

      <!-- Business Operations -->
      <v-list-group
        :value="menuState.operations"
        @input="updateMenuState('operations', $event)"
        prepend-icon="mdi-store"
        v-can="['pos.read', 'item.read', 'warehouse.read', 'category.read', 'store.read']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Business Operations</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Business Operations</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in businessOperations"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Inventory Management -->
      <v-list-group
        :value="menuState.inventory"
        @input="updateMenuState('inventory', $event)"
        prepend-icon="mdi-package-variant-closed"
        v-can="['supply.read', 'modifier.read', 'variant.read']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Inventory Management</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Inventory Management</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in inventoryManagement"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Sales & Tickets -->
      <v-list-group
        :value="menuState.sales_tickets"
        @input="updateMenuState('sales_tickets', $event)"
        prepend-icon="mdi-sale-outline"
        v-can="['sale.read', 'ticket.read']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Sales & Tickets</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Sales & Tickets</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in salesTickets"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Reservation System -->
      <v-list-group
        :value="menuState.reservations"
        @input="updateMenuState('reservations', $event)"
        prepend-icon="mdi-calendar"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Reservation System</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Reservation System</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in reservationSystem"
          :key="item.name"
          :to="{ name: item.route }"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- People & Relationships -->
      <v-list-group
        :value="menuState.people"
        @input="updateMenuState('people', $event)"
        prepend-icon="mdi-account-group"
        v-can="['user.read', 'employee.read', 'customer.read', 'supplier.read']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>People & Relationships</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>People & Relationships</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in peopleRelationships"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Financial Management -->
      <v-list-group
        :value="menuState.finance"
        @input="updateMenuState('finance', $event)"
        prepend-icon="mdi-cash-multiple"
        v-can="['deposit.read', 'withdrawal.read', 'debt.read', 'payroll.read', 'discount.read', 'tax.read', 'account.read-category']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Financial Management</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Financial Management</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in financialManagement"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Reports & Analytics -->
      <v-list-group
        :value="menuState.reports"
        @input="updateMenuState('reports', $event)"
        prepend-icon="mdi-chart-bar"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Reports & Analytics</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Reports & Analytics</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in reportsAnalytics"
          :key="item.name"
          :to="{ name: item.route }"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Administration -->
      <v-list-group
        :value="menuState.admin"
        @input="updateMenuState('admin', $event)"
        prepend-icon="mdi-shield"
        v-can="['role.read', 'session.read', 'service.read', 'payment_method.read']"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Administration</v-list-item-title>
          </v-list-item-content>
          <v-tooltip v-if="miniSync" right>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
            </template>
            <span>Administration</span>
          </v-tooltip>
        </template>

        <v-list-item
          v-for="item in administration"
          :key="item.name"
          :to="{ name: item.route }"
          v-can="item.permission"
          class="pl-11"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!-- Settings -->
      <v-list-item
        :to="{ name: 'config_org' }"
        v-can="'setting.read'"
        class="mt-1"
      >
        <v-list-item-icon>
          <v-icon>mdi-cogs</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('setting.name') }}</v-list-item-title>
        </v-list-item-content>
        <v-tooltip v-if="miniSync" right>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="mini-tooltip-target"></span>
          </template>
          <span>{{ $t('setting.name') }}</span>
        </v-tooltip>
      </v-list-item>
    </v-list>

    <!-- Logo at bottom for normal variant -->
    <template v-slot:append v-if="!miniSync">
      <v-divider />
      <v-list dense class="pa-2">
        <v-list-item>
          <v-list-item-avatar size="32">
            <img src="@/assets/logo/icon-geebung.png" alt="Logo">
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">
              Salamanda ERP
            </v-list-item-title>
            <v-list-item-subtitle class="caption">
              v{{ version }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'SidebarNew',
  props: {
    drawer: {
      type: Boolean,
      default: true
    },
    mini: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      version: '1.0.0',
      menuState: {
        operations: false,
        inventory: false,
        reservations: false,
        people: false,
        finance: false,
        reports: false,
        admin: false
      },
      businessOperations: [
        { name: 'pos', route: 'poses', icon: 'mdi-point-of-sale', title: 'link.pos', permission: 'pos.read' },
        { name: 'items', route: 'items', icon: 'mdi-shopping', title: 'link.article', permission: 'item.read' },
        { name: 'warehouses', route: 'warehouses', icon: 'mdi-warehouse', title: 'link.warehouse', permission: 'warehouse.read' },
        { name: 'categories', route: 'categories', icon: 'mdi-shape', title: 'link.family', permission: 'category.read' },
        { name: 'stores', route: 'stores', icon: 'mdi-store', title: 'link.store', permission: 'store.read' }
      ],
      inventoryManagement: [
        { name: 'supplies', route: 'supplies', icon: 'mdi-truck-delivery', title: 'link.supply', permission: 'supply.read' },
        { name: 'modifiers', route: 'modifiers', icon: 'mdi-tag-multiple', title: 'link.modifier', permission: 'modifier.read' }
      ],
      salesTickets: [
        { name: 'sales', route: 'sales', icon: 'mdi-sale', title: 'link.sale', permission: 'sale.read' },
        { name: 'tickets', route: 'tickets', icon: 'mdi-ticket-confirmation', title: 'link.ticket', permission: 'ticket.read' }
      ],
      reservationSystem: [
        { name: 'bookings', route: 'bookings', icon: 'mdi-calendar-check-outline', title: 'Bookings' },
        { name: 'rooms', route: 'rooms', icon: 'mdi-door', title: 'Rooms' }
      ],
      peopleRelationships: [
        { name: 'users', route: 'users', icon: 'mdi-account-key', title: 'link.user', permission: 'user.read' },
        { name: 'employees', route: 'employees', icon: 'mdi-account-group', title: 'link.employee', permission: 'employee.read' },
        { name: 'customers', route: 'customers', icon: 'mdi-account-multiple', title: 'link.customer', permission: 'customer.read' },
        { name: 'suppliers', route: 'suppliers', icon: 'mdi-truck', title: 'link.supplier', permission: 'supplier.read' }
      ],
      financialManagement: [
        { name: 'account_category', route: 'category', icon: 'mdi-chart-tree', title: 'link.acc_category', permission: 'account.read-category' },
        { name: 'deposits', route: 'deposit', icon: 'mdi-cash-plus', title: 'link.deposit', permission: 'deposit.read' },
        { name: 'withdrawals', route: 'withdrawal', icon: 'mdi-cash-minus', title: 'link.withdrawal', permission: 'withdrawal.read' },
        { name: 'debts', route: 'debts', icon: 'mdi-currency-usd-off', title: 'link.debt', permission: 'debt.read' },
        { name: 'salaries', route: 'salaries', icon: 'mdi-briefcase-account', title: 'link.salary', permission: 'payroll.read' },
        { name: 'discounts', route: 'discounts', icon: 'mdi-percent', title: 'link.discount', permission: 'discount.read' },
        { name: 'taxes', route: 'taxes', icon: 'mdi-calculator', title: 'link.tax', permission: 'tax.read' }
      ],
      reportsAnalytics: [
        { name: 'sales-summary', route: 'sales-summary', icon: 'mdi-chart-line', title: 'link.sales_summary' },
        { name: 'sales-by-item', route: 'sales-by-item', icon: 'mdi-chart-bar', title: 'link.sales_by_item' },
        { name: 'sales-by-category', route: 'sales-by-category', icon: 'mdi-chart-pie', title: 'link.sales_by_category' },
        { name: 'sales-by-employee', route: 'sales-by-employee', icon: 'mdi-account-filter', title: 'link.sales_by_employee' },
        { name: 'sales-by-payment', route: 'sales-by-payment-method', icon: 'mdi-credit-card-check', title: 'link.sales_by_payment_method' },
        { name: 'sales-by-modifier', route: 'sales-by-modifier', icon: 'mdi-chart-donut', title: 'link.sales_by_modifier' },
        { name: 'sales-by-variant', route: 'sales-by-variant', icon: 'mdi-chart-scatter-plot', title: 'link.sales_by_variant' },
        { name: 'query-sales', route: 'query-sales', icon: 'mdi-database-search', title: 'link.query_sales' },
        { name: 'query-data', route: 'query-data', icon: 'mdi-database-export', title: 'link.query_data' }
      ],
      administration: [
        { name: 'sessions', route: 'sessions', icon: 'mdi-timer-sand', title: 'link.session', permission: 'session.read' },
        { name: 'services', route: 'services', icon: 'mdi-room-service', title: 'link.service', permission: 'service.read' },
        { name: 'payment-methods', route: 'payment-methods', icon: 'mdi-credit-card-multiple', title: 'link.payment_method', permission: 'payment_method.read' },
        { name: 'roles', route: 'roles', icon: 'mdi-shield-account', title: 'link.role', permission: 'role.read' }
      ]
    }
  },
  computed: {
    ...mapState({
      user: state => state.Auth.user
    }),
    drawerSync: {
      get () {
        return this.drawer
      },
      set (val) {
        this.$emit('update:drawer', val)
      }
    },
    miniSync: {
      get () {
        return this.mini
      },
      set (val) {
        this.$emit('update:mini', val)
      }
    }
  },
  watch: {
    '$route' () {
      // Expand parent menu when child route is active
      this.expandActiveMenu()
    }
  },
  methods: {
    toggleDarkMode () {
      this.$emit('toggle-dark-mode')
    },
    updateMenuState (menu, value) {
      this.menuState[menu] = value
      this.saveMenuState()
    },
    saveMenuState () {
      localStorage.setItem('menuState', JSON.stringify(this.menuState))
    },
    loadMenuState () {
      const saved = localStorage.getItem('menuState')
      if (saved) {
        this.menuState = JSON.parse(saved)
      }
    },
    expandActiveMenu () {
      const currentRoute = this.$route.name

      // Check which menu group contains the current route
      if (this.businessOperations.some(item => item.route === currentRoute)) {
        this.menuState.operations = true
      } else if (this.inventoryManagement.some(item => item.route === currentRoute)) {
        this.menuState.inventory = true
      } else if (this.reservationSystem.some(item => item.route === currentRoute)) {
        this.menuState.reservations = true
      } else if (this.peopleRelationships.some(item => item.route === currentRoute)) {
        this.menuState.people = true
      } else if (this.financialManagement.some(item => item.route === currentRoute)) {
        this.menuState.finance = true
      } else if (this.reportsAnalytics.some(item => item.route === currentRoute)) {
        this.menuState.reports = true
      } else if (this.administration.some(item => item.route === currentRoute)) {
        this.menuState.admin = true
      }
    }
  },
  mounted () {
    this.loadMenuState()
    this.expandActiveMenu()
  }
}
</script>

<style scoped>
.sidebar-new {
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
}

.sidebar-new >>> .v-navigation-drawer__content {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.user-profile-section {
  background: rgba(25, 118, 210, 0.05);
  border-radius: 8px;
  margin: 8px;
}

.theme--dark .user-profile-section {
  background: rgba(25, 118, 210, 0.15);
}

.sidebar-new >>> .v-list-item {
  margin: 0 8px;
  border-radius: 8px;
  margin-bottom: 2px;
}

.sidebar-new >>> .v-list-group__header {
  margin: 0 8px;
  border-radius: 8px;
  margin-bottom: 2px;
}

.sidebar-new >>> .v-list-item--active {
  background: rgba(25, 118, 210, 0.12);
}

.sidebar-new >>> .v-list-item--active::before {
  opacity: 0;
}

.sidebar-new >>> .v-list-item--active .v-icon {
  color: #1976D2;
}

.theme--dark .sidebar-new >>> .v-list-item--active {
  background: rgba(25, 118, 210, 0.24);
}

.sidebar-new >>> .v-list-group__items {
  margin-bottom: 8px;
}

.sidebar-new >>> .v-list-item--link:hover {
  background: rgba(0, 0, 0, 0.04);
}

.theme--dark .sidebar-new >>> .v-list-item--link:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Mini variant tooltips */
.mini-tooltip-target {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

/* Smooth transitions */
.sidebar-new >>> .v-list-item__content,
.sidebar-new >>> .v-list-item__icon {
  transition: all 0.2s ease;
}

/* Custom scrollbar for dark mode */
.theme--dark .sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.theme--dark .sidebar-new >>> .v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}
</style>
